/**
 * Operational hierarchy widget — spec §F5.8, as the 2026-08-22 client review (R5) rebuilt it.
 *
 * ## What went, and why
 *
 * §F5.8 specified five rows — Countries, Regions, Communes, Watersheds, Partnerships — each
 * with a ☑ visibility checkbox and a ⦿ `name=replvl` radio, plus a caption explaining which
 * was which. R5: *"remove the 'View data by:' block (the 5 checkbox/radio rows) entirely.
 * Admin boundaries display by default. The 'Filter by:' cascade governs display + data +
 * filter (reporting level derives from the cascade)."*
 *
 * The rows had become a second geography control standing next to the cascade and quietly
 * disagreeing with it. A filter already unticked the rows outside itself and disabled them
 * (`visibilityForScope`), already narrowed the live radios to the levels its subtree had
 * units at (`scopeLevels`), and already re-checked a radio when it forced the level. What was
 * left for the reader to do with ten controls was to discover which four of them the filter
 * had not already decided — so the cascade decides all of them, and this widget is the
 * cascade plus a line saying what it derived.
 *
 * ## What the level is now
 *
 * A pure function of the scope, and it lives in the store where the scope does:
 *
 * | filter                | reporting level                     | boundaries drawn            |
 * |-----------------------|-------------------------------------|-----------------------------|
 * | none                  | regions (`NO_SCOPE_LEVEL`)          | countries + regions         |
 * | a country             | its regions                         | countries + regions         |
 * | a region              | its communes                        | that region + its communes  |
 * | a commune             | itself                              | that commune                |
 * | a watershed           | itself, or its partnerships (§M3.5) | watersheds + partnerships   |
 * | a partnership         | itself                              | partnerships                |
 *
 * — `setScope`/`clearScope` (`levelWithinScope`, `NO_SCOPE_LEVEL`) for the level, and
 * `useHierarchyStore.applyScope` (`visibilityForScope`) for the boundaries. Both were already
 * written and already driving the map; R5 deleted the controls that could contradict them.
 *
 * **§M3.5 survives intact.** `'watershed'` is still a *conditional* mode — MELS reports
 * B.2.1/C.2.1 per `watershed_id`, and an indicator without native watershed values must not
 * be painted at that stratum — so a watershed scope reports at watershed level when the
 * mapped indicator has those values and at partnership level when it does not. That is the
 * `reconcileReportingLevel` effect below, which is now the widget's only behaviour: it is
 * still the one component that reads both halves (the level, and the catalogue row that says
 * whether the level is meaningful).
 *
 * **One capability went with the radios**, and it is worth saying out loud rather than
 * discovering later: there is no longer a way to *ask* for the country-level choropleth (4
 * polygons) or for the programme-wide watershed one (11), because no cascade selection
 * implies them. Both remain valid store states and a `lv=` permalink still restores them —
 * see docs/NOTES.md.
 */
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Widget } from '../../components/Widget';
import { fetchIndicators, queryKeys } from '../../lib/api';
import { pendingPermalinkIds } from '../../lib/permalink';
import {
  levelLabelKey,
  levelWithinScope,
  PARTNERSHIP_LEVEL,
  useAppStore,
  WATERSHED_LEVEL,
} from '../../lib/state';
import { CascadeFilter } from './CascadeFilter';

export function OperationalHierarchy() {
  const { t } = useTranslation();
  const scope = useAppStore((s) => s.scope);
  const indicatorId = useAppStore((s) => s.indicator);
  const reportingLevel = useAppStore((s) => s.reportingLevel);
  const lastAdminLevel = useAppStore((s) => s.lastAdminLevel);
  const reconcileReportingLevel = useAppStore((s) => s.reconcileReportingLevel);

  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });
  const watershedReady = Boolean(
    indicatorId && indicators?.find((i) => i.id === indicatorId)?.has_watershed_values,
  );

  /**
   * §M3.5's catch-all: a permalink hydrates `reportingLevel` with `setState` before
   * `/indicators` exists, and `PermalinkSync` can drop the indicator a stale link named. This
   * is the one place that knows both halves — the level and the catalogue — so it reconciles
   * them once the catalogue has actually answered (`indicators` undefined would demote every
   * restored `lv=watershed` link on boot).
   *
   * **It waits for `PermalinkSync` to finish first** (R6 fix). Both effects fire off the same
   * `/indicators` landing, and this one renders with whatever indicator the *hash* named —
   * which `PermalinkSync` may be about to replace, because the link is stale or names a
   * demo-only row. Reconciling against that indicator demotes the level for an indicator that
   * is on its way out, and the demotion is one-way: `PermalinkSync`'s own later, correct call
   * has no path back up to `'watershed'`. So while a restored hash still has unconfirmed
   * indicator ids, the answer belongs to the component that is confirming them; it reconciles
   * in the same tick, and this effect re-runs on the corrected indicator immediately after.
   * (`pendingPermalinkIds` is empty on every load that carried no `ind=`/`ov=`/`biv=`, so
   * outside a permalink boot this gate is not there.)
   */
  useEffect(() => {
    if (!indicators) return;
    if (pendingPermalinkIds().indicators.length) return;
    reconcileReportingLevel(indicatorId, watershedReady);
  }, [indicators, indicatorId, watershedReady, reconcileReportingLevel]);

  // …and until that effect runs (one frame, or forever if the catalogue never answers) the
  // hint names the level it would fall back to, so the widget never claims the map is at a
  // stratum the mapped indicator has no rows in. Scope-aware for the same reason
  // `demotedLevel` is: inside a watershed there are no admin units to fall back to.
  const shownLevel =
    reportingLevel === WATERSHED_LEVEL && !watershedReady
      ? levelWithinScope(scope, lastAdminLevel, PARTNERSHIP_LEVEL)
      : reportingLevel;

  return (
    <Widget id="boundsWidget" title={t('hierarchy.title')} icon="basemap">
      <div id="bwBody">
        <CascadeFilter />
        {/*
          The one thing the deleted radios said that the cascade does not: which level the
          numbers are being reported at. It is a *statement*, not a control — the reader
          changes it by changing the filter — so it reads as a sentence rather than a row of
          inputs, and the §F5.2 pane's "mapped at … level" hint says the same thing in the
          same words.
        */}
        <div className="bw-derived" data-derivedlevel={String(shownLevel)}>
          {t('hierarchy.derivedLevel', { level: t(levelLabelKey(shownLevel)).toLowerCase() })}
        </div>
      </div>
    </Widget>
  );
}

export default OperationalHierarchy;
